// @flow
import React, {createContext, useContext, useReducer, useState} from 'react';
import {PedigreeNodeDto} from "../../api/dtos/pedigree-dto";
import {PedigreeService} from "../../services/pedigree-service";
import {Button, Card, CardActions, CardContent, CardHeader, IconButton, Stack, Typography} from "@mui/material";
import {AddCircleOutline, ArrowBack, Downloading, Edit, FileDownload, RemoveCircleOutline} from "@mui/icons-material";
import {JsonAnimal} from "../../api/generated/rest-dto";
import {Sex, SexValues} from "../../api/generated/herd-animal";
import {useTranslation} from "react-i18next";
import {ErrorBox, InfoBox} from '../Common/alerts';
import {useQuery} from "@tanstack/react-query";
import {PedigreeDocument} from "../AnimalDashboard/Pdf/pedigree-pdf";
import {PDFDownloadLink} from "@react-pdf/renderer";
import {SexIcon} from '../../icons/SexIcon';
import {RegistryIcon} from "../../icons";
import {LoadingSuspense} from "../Common/loading-suspense";
import CardActionArea from "@mui/material/CardActionArea";
import {useNav} from "../Common/hooks/use-nav";
import RouteService from "../../services/route-service";
import {LoadingFallback} from "../Common/loading-fallback";

type PedigreeProps = {
    animal: JsonAnimal;
    editable?: boolean;
    expandedRedirectUrl?: string;
}

type PedigreeNodeProps = {
    node: PedigreeNodeDto | undefined | null;
};

const HistoryContext = createContext<string | undefined>(undefined);
const SelectPedigreeContext = createContext<(panonId: string) => void>(() => {});

const useToggle = (initialValue: boolean) => useReducer(state => !state, initialValue);

const PedigreeSexIcon = ({sex}: { sex: Sex }) => (
    <SexIcon sex={sex} color={sex === SexValues.MALE ? "mediumblue" : "firebrick"}/>
);

const PedigreeElementDetails = ({node}: PedigreeNodeProps) => {
    const {t} = useTranslation();

    if (!node) {
        return (
            <Stack direction="column">
                <Typography>unbekannt</Typography>
                <Typography>&nbsp;</Typography>
            </Stack>
        );
    }

    return (
        <Stack direction="column">
            <Typography>{t('color.' + node.color)}</Typography>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}>
                {node.registries.length > 0 ? (
                        <>
                            <RegistryIcon fontSize="small" titleAccess={t("registered_at")}/>
                            <Typography>{node.registries}</Typography>
                        </>
                    )
                    : (
                        <>
                            <RegistryIcon fontSize="small" color="warning"/>
                            <Typography color="warning">nicht registriert</Typography>
                        </>
                    )}
            </Stack>
        </Stack>
    );
};

const PedigreeElement = ({node, editable}: PedigreeNodeProps & { editable?: boolean }) => {
    const setPedigree = useContext(SelectPedigreeContext);
    const expandedRedirectUrl = useContext(HistoryContext);
    const nav = useNav();

    return (
        <Card variant="outlined" sx={{maxWidth: '15em'}}>
            <CardActionArea disabled={!node} onClick={() => node && setPedigree(node.panonId)}>
                <CardHeader
                    title={node ? (
                        <Stack direction="row" alignItems="top">
                            <PedigreeSexIcon sex={node.sex}/>
                            <Typography fontWeight="bold">{node.fullName}</Typography>
                        </Stack>
                    ) : <>&mdash;</>}
                />
                <CardContent>
                    <PedigreeElementDetails node={node}/>
                </CardContent>
            </CardActionArea>
            {editable && (
                <CardActions>
                    {node && (
                        <Button size="small"
                                startIcon={<Edit/>}
                                onClick={() => nav.push(RouteService.ANIMAL_CHANGE_ANCESTRY, {panonId: node.panonId}, {expandedRedirectUrl})}>
                            Edit
                        </Button>
                    )}
                </CardActions>
            )}
        </Card>
    );
};

const parentsOf = (nodes: (PedigreeNodeDto | undefined | null)[]) => nodes.flatMap(node => [
    node?.sire,
    node?.dam,
]);

const Generation = ({animals, collapsed, editable}: {
    editable?: boolean,
    collapsed?: boolean,
    animals: (PedigreeNodeDto | undefined | null)[]
}) => {
    const [expanded, toggleExpanded] = useToggle(!collapsed);

    if (animals.every(animal => !animal)) {
        return <Stack></Stack>;
    }

    return (
        <Stack direction="column" flexShrink={0}>
            <IconButton onClick={toggleExpanded} sx={{flexGrow: 0, flexShrink: 1}}>
                {!expanded ? <AddCircleOutline/> : <RemoveCircleOutline/>}
            </IconButton>
            {expanded ? (
                <Stack spacing={2} flexShrink={0} flexGrow={1} justifyContent="space-around">
                    {animals.map(animal => <PedigreeElement key={animal?.panonId} node={animal} editable={editable}/>)}
                </Stack>
            ) : (
                <Stack spacing={2} flexShrink={0} flexGrow={1} justifyContent="space-around" alignItems="center" onClick={toggleExpanded}>
                    {animals.map(animal => animal
                        ? <PedigreeSexIcon key={animal.panonId} sex={animal.sex}/>
                        : <div>&nbsp;</div>)}
                </Stack>
            )}
        </Stack>
    );
};

const Pedigree: React.FC<PedigreeProps> = ({animal, editable, expandedRedirectUrl}) => {

    const initialPanonId = animal.panonIdentifier.id;
    const [selectedPedigree, setSelectedPedigree] = useState(initialPanonId);

    const {data: pedigree, isLoading, isError} = useQuery({
        queryKey: ['pedigree', {id: selectedPedigree}],
        queryFn: () => PedigreeService.loadPedigreeByPanonId(selectedPedigree),
    });

    if (isLoading) {
        return <LoadingFallback message="Stammbaum wird geladen"/>;
    }

    if (isError) {
        return <ErrorBox>Fehler beim Laden des Stammbaums.</ErrorBox>
    }

    if (!pedigree) {
        return <Typography>Keine Stammbauminformation vorhanden!</Typography>;
    }

    const rootAnimalSelected = initialPanonId === selectedPedigree;

    const parents = parentsOf([pedigree.root]);
    const hasParents = parents.every(parent => parent);

    const grandparents = parentsOf(parents);
    const greatGrandparents = parentsOf(grandparents);
    return (
        <Stack gap={1}>
            {rootAnimalSelected && !hasParents && <InfoBox>Keine Elterntiere eingetragen</InfoBox>}

            <LoadingSuspense>
                <HistoryContext.Provider value={expandedRedirectUrl}>
                    <SelectPedigreeContext.Provider value={setSelectedPedigree}>
                        <Stack direction="row" spacing={3} sx={{width: '100%', overflowX: 'auto'}}>
                            {rootAnimalSelected ? null : (
                                <IconButton onClick={() => setSelectedPedigree(initialPanonId)}>
                                    <ArrowBack/>
                                </IconButton>
                            )}

                            <Generation editable={editable} animals={[pedigree.root]}/>
                            <Generation editable={editable} animals={parents}/>
                            <Generation editable={editable} animals={grandparents}/>
                            <Generation editable={editable} animals={greatGrandparents} collapsed/>
                            <Generation editable={editable} animals={parentsOf(greatGrandparents)} collapsed/>
                        </Stack>
                    </SelectPedigreeContext.Provider>
                </HistoryContext.Provider>
            </LoadingSuspense>

            <PDFDownloadLink document={<PedigreeDocument pedigree={pedigree}/>}
                             fileName={`${animal.herdCode}_${animal.name}_pedigree.pdf`}
                             style={{marginLeft:'auto'}}>
                {({loading}) => (
                    <Button startIcon={loading ? <Downloading/> : <FileDownload/>}
                            variant="contained"
                            disabled={loading}>
                        {loading ? 'PDF wird generiert' : 'Stammbaum als PDF downloaden'}
                    </Button>
                )}
            </PDFDownloadLink>
        </Stack>
    );
};

export default Pedigree
